import { IonIcon, IonText } from '@ionic/react';
import VStack from 'components/VStack';
import { star, locationOutline, flagOutline, pencil } from 'ionicons/icons';
import { User } from 'models/User';
import { ChangeEvent, useRef } from 'react';
import { Trans } from 'react-i18next';
import { logger } from 'services/Logger';

interface ProfileTopBarProps {
  onclick: () => void;
  user: User;
  hasPencil?: boolean;
  onPhotoSelected?: (file: File) => void;
}

const ProfileTopBar: React.FunctionComponent<ProfileTopBarProps> = (props) => {
  const { onclick, user, onPhotoSelected, hasPencil } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Verhindert bubbling
    if (!onPhotoSelected) return; // Kein Upload wenn onPhotoSelected nicht definiert
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      logger.warn('fileInputRef.current is null');
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      if (typeof onPhotoSelected === 'function') {
        onPhotoSelected(e.target.files[0]);
      } else {
        logger.warn('[ProfileTopBar] onPhotoSelected ist keine Funktion:', onPhotoSelected);
      }
    }
  };

  return (
    <div className="person-container">
      <div
        className="person-avatar"
        onClick={onPhotoSelected ? handleAvatarClick : undefined}
        style={{ 
          cursor: onPhotoSelected ? 'pointer' : 'default', 
          position: 'relative' 
        }}
        tabIndex={onPhotoSelected ? 0 : undefined}
        onKeyDown={onPhotoSelected ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') handleAvatarClick(e as any);
        } : undefined}
      >
        {user.photoURL ? (
          <>
            <img
              src={user.photoURL}
              alt="avatar"
              style={{ display: 'block', width: '100%', height: 'auto', borderRadius: '50%' }}
              onError={(e) => {
                // Fallback-Icon anzeigen, wenn das Bild nicht geladen werden kann
                (e.target as HTMLImageElement).style.display = 'none';
                const fallback = document.createElement('span');
                fallback.className = 'avatar-fallback-icon';
                fallback.innerHTML = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21h13a2 2 0 0 0 2-2v-1a7 7 0 0 0-7-7h-1a7 7 0 0 0-7 7v1a2 2 0 0 0 2 2z"/></svg>';
                (e.target as HTMLImageElement).parentElement?.appendChild(fallback);
              }}
            />
            {/* Overlay mit weißem Stift, nur wenn hasPencil true */}
            {hasPencil && (
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(0,0,0,0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                <IonIcon icon={pencil} style={{ fontSize: 36, color: 'white', opacity: 0.95, transform: 'rotate(-60deg)' }} />
              </span>
            )}
          </>
        ) : (
          <span style={{ position: 'relative', display: 'inline-block' }}>
            <IonIcon
              src="/assets/icons/Person_Iro.svg"
              className="person-avatar-icon"
              style={{ width: 64, height: 64 }}
            />
            {/* Overlay mit weißem Stift auch für das Icon, nur wenn hasPencil true */}
            {hasPencil && (
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(0,0,0,0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                <IonIcon icon={pencil} style={{ fontSize: 36, color: 'white', opacity: 0.95, transform: 'rotate(60deg)' }} />
              </span>
            )}
          </span>
        )}
        {onPhotoSelected && (
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleFileChange}
          />
        )}
      </div>
      <div className="person-info">
        <div className="person-header">
          <VStack>
            <IonText className="person-name">
              {user.displayName ?? user.email}
            </IonText>
            <div className="person-score">
              <IonIcon icon={star} className="score-icon" />
              <IonText className="score-text">
                <Trans i18nKey="TOTAL_SCORE" defaults="Gesamtscore:" />{' '}
                {user.score ?? 0}
              </IonText>
            </div>
          </VStack>
        </div>
        <div className="person-meta">
          {user.address?.city && (
            <div className="person-meta-item">
              <IonIcon icon={locationOutline} className="person-meta-icon" />
              <IonText className="person-meta-text">
                {user.address.city}
              </IonText>
            </div>
          )}
          {user.address?.country && (
            <div className="person-meta-item">
              <IonIcon icon={flagOutline} className="person-meta-icon" />
              <IonText className="person-meta-text">
                {user.address.country}
              </IonText>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileTopBar;
